import React, { useContext, useEffect, useMemo, useState } from 'react';
import { getFeatureFlags } from '../api';
import useLocalStorage from '../../utils/useLocalStorage';

const normalizeFeatureFlagName = (value) => (value ?? '').toString().toLowerCase();

const FeatureFlagsContext = React.createContext();

export function FeatureFlagsProvider({ children }) {
	const [companyId] = useLocalStorage('company_id');
	const [flags, setFlags] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const fetchFeatureFlags = async () => {
		try {
			setLoading(true);
			setError(null);
			const result = await getFeatureFlags();
			setFlags(Array.isArray(result) ? result.filter(f => f.active) : []);
		} catch (ex) {
			console.log(ex);
			setFlags([]);
			setError(ex);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchFeatureFlags();
	}, [companyId]);

	const normalizedFlags = useMemo(
		() => new Set(flags.map((flag) => normalizeFeatureFlagName(flag?.name ?? flag))),
		[flags]
	);

	const hasFeatureFlag = (name) => normalizedFlags.has(normalizeFeatureFlagName(name));

	const value = {
		featureFlags: flags,
		hasFeatureFlag,
		loading,
		error,
		refetchFeatureFlags: fetchFeatureFlags,
	};

	return (
		<FeatureFlagsContext.Provider value={value}>
			{children}
		</FeatureFlagsContext.Provider>
	);
}

export const useFeatureFlag = (featureName) => {
	const context = useContext(FeatureFlagsContext);
	
	if (!context) {
		throw new Error('useFeatureFlag must be used within FeatureFlagsProvider');
	}

	const { hasFeatureFlag, featureFlags, loading, error, refetchFeatureFlags } = context;

	return {
		featureFlags,
		isFeatureEnabled: featureName ? hasFeatureFlag(featureName) : false,
		hasFeatureFlag,
		loading,
		error,
		refetchFeatureFlags,
	};
};

export default useFeatureFlag;
